import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  changeDeductibleLegend,
  saveDeductibleSelected,
  saveSelectedReimbursement,
  selectedPlan,
} from '../../../actions';

import { SelectorPills } from './SelectorPills';
import styles from './DeductibleReimbursement.module.css';
import { useProp103Features } from '../../../hooks';
import { FilterByProp103Dropdown } from './FilterByProp103Dropdown';

const ULTIMATE_PLAN = 2;

export const DeductibleReimbursement = ({
  containerClass = '',
  plans,
  quoteSelected,
}) => {
  const dispatch = useDispatch();
  const store = useSelector(({ quoting }) => quoting);
  const { t } = useTranslation('quoting');
  const prop103 = useProp103Features();

  const [deductibles, setDeductibles] = useState([]);
  const [reimbursements, setReimbursements] = useState([]);

  const plansItems = useMemo(
    () => {
      const plansMapped = plans.map((item) => ({
        ...item,
        id: item.Plan,
        value: item.MaxAnnual,
      }));
      return [
        ...plansMapped
          .filter((item) => item.MaxAnnual.includes('$'))
          .sort((a, b) => {
            const numA = parseInt(a.MaxAnnual.replace(/[$,]/g, ''), 10);
            const numB = parseInt(b.MaxAnnual.replace(/[$,]/g, ''), 10);
            return numA - numB;
          }),
        ...plansMapped.filter((item) => !item.MaxAnnual.includes('$')),
      ];
    },
    [plans],
  );

  const isUltimateSelected = quoteSelected.selectedPlanIndex
    === ULTIMATE_PLAN;

  /** show heads-up for all plans except Unlimited (Ultimate) */
  const showHeadsUpLegend = useMemo(() => {
    const quotePlan = store.plans
      .find((item) => item.id === quoteSelected.selectedPlan);

    if (!quotePlan?.maxAnnual.includes('$')) {
      // ultimate plan
      return false;
    }

    return true;
  }, [store.plans, quoteSelected]);

  const updateDeductible = useCallback(({ id, value }) => {
    dispatch(saveDeductibleSelected({ description: value, id }));
  }, [dispatch]);

  const updateReimbursement = useCallback(() => {
    const plan = plans.find((item) => item.Plan === quoteSelected.selectedPlan);

    const ratingOptions = plan.RatingOptions
      .filter((rating) => rating.DeductibleId === quoteSelected.deductibleId);
    const reimbursementsFilter = ratingOptions
      .filter((item) => item.DeductibleId === quoteSelected.deductibleId)
      .map((item) => ({
        available: !item.Invalid,
        description: item.ReimbursementName,
        id: item.ReimbursementId,
      }));
    const reimbursementAvailable = reimbursementsFilter
      .reverse().find((item) => item.available);
    const { description, id } = reimbursementAvailable;

    dispatch(saveSelectedReimbursement({ description, id }));
  }, [dispatch, plans, quoteSelected.selectedPlan, quoteSelected.deductibleId]);

  const onReimbursementChange = useCallback(({ id, label }) => {
    dispatch(saveSelectedReimbursement({ description: label, id }));
    if (prop103) {
      dispatch(selectedPlan({
        planAmount: quoteSelected.selectedPlanAmount,
        planId: quoteSelected.selectedPlan,
        planPackagedId: -1,
      }));
    }
  }, [dispatch, prop103, quoteSelected]);

  const onDeductibleChange = useCallback(({ id, label }) => {
    updateDeductible({ id, value: label });
    if (prop103) {
      dispatch(selectedPlan({
        planAmount: quoteSelected.selectedPlanAmount,
        planId: quoteSelected.selectedPlan,
        planPackagedId: -1,
      }));
    }
  }, [dispatch, prop103, quoteSelected, updateDeductible]);

  const onCoverageChange = useCallback(({ MaxAnnual, Plan }) => {
    dispatch(selectedPlan({
      planAmount: MaxAnnual,
      planId: Plan,
      planPackagedId: -1,
    }));
  }, [dispatch]);

  useEffect(() => {
    if (plans && !plans.length) {
      return;
    }

    const {
      deductibleId,
      reimbursementId,
      reimbursementPercentage,
    } = quoteSelected;

    const plan = plans.find((item) => item.Plan === quoteSelected.selectedPlan);
    if (!plan) {
      return;
    }

    const ratingOptions = plan.RatingOptions
      .filter((rating) => rating.ReimbursementId === reimbursementId);
    const deductiblesFilter = ratingOptions
      .filter((item) => item.ReimbursementId === reimbursementId)
      .map((item) => ({
        available: !item.Invalid,
        disabled: item.Invalid,
        id: item.DeductibleId,
        value: item.DeductibleName,
      }));
    const deductiblesAvailables =
      deductiblesFilter.filter((item) => item.available);
    const currentDeductible =
      deductiblesFilter.find((item) => item.id === deductibleId) || {};
    let deductibleLegend = '';

    if (!deductiblesAvailables.length) {
      updateReimbursement();
      return;
    }

    if (deductiblesAvailables.length < deductiblesFilter.length) {
      const names = deductiblesAvailables.map((item) => item.value);

      let nameLegend = '';

      if (names.length === 1) {
        nameLegend = names.join(t(' and '));
      } else {
        names.forEach((item, index) => {
          const concat = names.length - 1 === index ? ' and ' : ', ';

          nameLegend = nameLegend
            ? `${nameLegend}${concat}${item}` : ` ${item}`;
        });
      }

      deductibleLegend = `${reimbursementPercentage} `
        + 'reimbursement is available with deductibles of'
        + ` ${nameLegend}`;
    }

    dispatch(changeDeductibleLegend(deductibleLegend));

    if (!currentDeductible.available) {
      updateDeductible(
        [...deductiblesFilter].reverse().find((item) => item.available),
      );
    }
    if (prop103) {
      setDeductibles([...deductiblesFilter].reverse());
      return;
    }

    setDeductibles(deductiblesFilter);
  }, [
    dispatch,
    plans,
    quoteSelected,
    t,
    updateDeductible,
    updateReimbursement,
    prop103,
  ]);

  useEffect(() => {
    if (plans && !plans.length) {
      return;
    }

    const plan = plans.find((item) => item.Plan === quoteSelected.selectedPlan);
    if (!plan) {
      return;
    }

    const ratingOptions = plan.RatingOptions
      .filter((rating) => rating.DeductibleId === quoteSelected.deductibleId);
    const reimbursementsFilter = ratingOptions
      .filter((item) => item.DeductibleId === quoteSelected.deductibleId)
      .map((item) => ({
        disabled: item.Invalid,
        id: item.ReimbursementId,
        value: item.ReimbursementName,
      }));

    // FIX ME, fix some plan (ultimate), blocking 100% reimbursement
    // const lastReimbursement =
    // reimbursementsFilter[reimbursementsFilter.length - 1] || {};
    // const reimbursement100Locked = plan.RatingOptions
    //   .filter((rating) => rating.ReimbursementId === lastReimbursement.id)
    //   .reduce((prev, next) => prev && next.Invalid, true);

    // setValidateSelector(!reimbursement100Locked);
    // FIX ME, fix some plan (ultimate), blocking 100% reimbursement

    setReimbursements(reimbursementsFilter);
  }, [plans, quoteSelected.deductibleId, quoteSelected.selectedPlan]);

  const headsUpClass = prop103
    ? styles.deductibleLegendProp103
    : styles.deductibleLegend;

  const deductibleLegendClass = prop103
    ? styles.deductibleLegendProp103
    : styles.deductibleLegend;

  if (!prop103 && (!quoteSelected.selectedPlanPackaged
    || quoteSelected.selectedPlanPackaged > 0)) {
    return showHeadsUpLegend ? (
      <p aria-live="polite" className={headsUpClass}>
        {t('prepackagedPlans.headsUp')}
      </p>
    ) : null;
  }

  return (
    <div className={styles.container}>
      <div
        className={`${styles.containerDiv} ${styles.wrapper}`
          + ` ${prop103 ? styles.wrapperProp103 : ''} ${containerClass}`}
      >
        {prop103
          ? <FilterByProp103Dropdown />
          : null}

        <SelectorPills
          items={plansItems}
          onChange={onCoverageChange}
          title={t('prepackagedPlans.annualCoverage')}
          tooltipDescription={t('prepackagedPlans.annualCoverageToooltip')}
          value={{
            label: quoteSelected.selectedPlanAmount,
            value: quoteSelected.selectedPlan,
          }}
        />

        {!prop103 && (
          <p aria-live="polite" className={headsUpClass}>
            {showHeadsUpLegend && t('prepackagedPlans.headsUp')}
          </p>
        )}

        <SelectorPills
          items={deductibles}
          onChange={onDeductibleChange}
          title={t('prepackagedPlans.deductible')}
          tooltipDescription={t('prepackagedPlans.deductibleTooltip')}
          value={{
            label: quoteSelected.deductibleQuantity,
            value: quoteSelected.deductibleId,
          }}
        />

        {!prop103 && (
          <span aria-live="polite" className={deductibleLegendClass}>
            {store.deductibleLegend}
          </span>
        )}

        <SelectorPills
          items={reimbursements}
          onChange={onReimbursementChange}
          title={t('prepackagedPlans.reimbursement')}
          tooltipDescription={t('prepackagedPlans.reimbursementTooltip')}
          value={{
            label: quoteSelected.reimbursementPercentage,
            value: quoteSelected.reimbursementId,
          }}
        />

        {!prop103 && (
          <span aria-live="polite" className={deductibleLegendClass}>
            {isUltimateSelected
              && t('prepackagedPlans.100ReimbursementNoAvailable')}
          </span>
        )}
      </div>
    </div>
  );
};
